import React, { MouseEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import { MetaFilterSelectedData, MetaFilterSelectedDate, MetaFilterTypes, NumbersRange } from '@modules/search/types'
import { SelectChangeEvent } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'

import searchApi from '../../../api'
import { META_FILTER_LABELS_MAP, RECENT_LABELS } from '../constants'
import DateRangeSelect from './DateRangeSelect'
import MetaFilterPopover from './MetaFilterPopover'
import {
  MetaFilterDivider,
  MetaFilterSelectArrow,
  MetaFilterSelectComponent,
  MetaFilterSelectContent,
  MetaFilterSelectIcon,
  MetaFilterSelectLabel,
  MetaFilterSelectMenuItem,
  MetaFilterSelectSubmit,
  MetaFilterSelectType,
  MetaFilterSelectWrapper,
  MetaFilterWrapper,
} from './MetaFilterSelect.styles'

export interface MetaFilterSelectProps {
  handleSelectMetaFilter: (filterData: MetaFilterSelectedData) => void
  selectedFilter: MetaFilterSelectedData
  eventsDatesRange: NumbersRange
  isFilterByNewEnabled?: boolean
}

const MenuProps = {
  MenuListProps: {
    style: {
      padding: '15px',
    },
  },
}

const MetaFilterSelect = (props: MetaFilterSelectProps) => {
  const { handleSelectMetaFilter, selectedFilter, eventsDatesRange, isFilterByNewEnabled } = props
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [metaFilterSelectOpen, setMetaFilterSelectOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null)
  const [metaFilterType, setMetaFilterType] = useState<MetaFilterTypes>(
    MetaFilterTypes[selectedFilter.type as keyof typeof MetaFilterTypes] || MetaFilterTypes.DEFAULT,
  )
  const [selectedType, setSelectedType] = useState<MetaFilterTypes>(
    MetaFilterTypes[selectedFilter.type as keyof typeof MetaFilterTypes] || MetaFilterTypes.DEFAULT,
  )

  const [dateRangeStart, setDateRangeStart] = useState(eventsDatesRange.min)
  const [dateRangeEnd, setDateRangeEnd] = useState(eventsDatesRange.max)

  const metaFilterPopoverOpen = Boolean(anchorEl)
  const id = metaFilterPopoverOpen ? 'metafilter-popover' : undefined
  const recentItemOptions = RECENT_LABELS.map(item => ({ label: t(item.label), value: item.value }))

  useEffect(() => {
    const type = (selectedFilter.type as keyof typeof MetaFilterTypes) ?? MetaFilterTypes.DEFAULT

    if (type === MetaFilterTypes.BY_DATE_RANGE) {
      const data = selectedFilter?.value as MetaFilterSelectedDate
      setDateRangeStart(data.from)
      setDateRangeEnd(data.to)
    }

    if (type === MetaFilterTypes.BY_NEW_ITEMS) {
      queryClient.invalidateQueries({ queryKey: [searchApi.search.key] })
    }

    setMetaFilterType(MetaFilterTypes[type])
    setSelectedType(MetaFilterTypes[type])
  }, [selectedFilter])

  useEffect(() => {
    if (!isFilterByNewEnabled && metaFilterType === MetaFilterTypes.BY_NEW_ITEMS) {
      setSelectedType(MetaFilterTypes.DEFAULT)
      setMetaFilterType(MetaFilterTypes.DEFAULT)
    }
  }, [isFilterByNewEnabled, metaFilterType])

  const handleOpenMetaFilter = (event: MouseEvent<HTMLElement>) => {
    const target = event.currentTarget as HTMLElement
    setAnchorEl(target)
  }

  const handleCloseMetaFilter = () => {
    setAnchorEl(null)
  }

  const handleOpenSelect = () => {
    setMetaFilterSelectOpen(true)
  }

  const handleCloseSelect = () => {
    setMetaFilterSelectOpen(false)
  }

  const handleSubmitFilterData = (event: SelectChangeEvent<unknown>) => {
    const target = event.target as HTMLInputElement
    handleSelectMetaFilter({ type: MetaFilterTypes[metaFilterType], value: target.value })
  }

  const handleSubmitDateRange = () => {
    handleSelectMetaFilter({ type: MetaFilterTypes.BY_DATE_RANGE, value: { from: dateRangeStart, to: dateRangeEnd } })
  }
  const isSelectedTypeOneOf = (data: MetaFilterTypes[]) => {
    return data.some(type => type === metaFilterType)
  }

  return (
    <MetaFilterWrapper>
      <MetaFilterSelectWrapper>
        <MetaFilterSelectContent
          $open={metaFilterPopoverOpen || metaFilterSelectOpen}
          $isNewItem={metaFilterType === MetaFilterTypes.BY_NEW_ITEMS}
          data-testid="meta-filter-content"
        >
          <MetaFilterSelectType onClick={handleOpenMetaFilter}>
            <MetaFilterSelectLabel
              $isDefaultLabel={isSelectedTypeOneOf([MetaFilterTypes.DEFAULT])}
              $isNewItemLabel={metaFilterType === MetaFilterTypes.BY_NEW_ITEMS}
            >
              {t(META_FILTER_LABELS_MAP[metaFilterType])}
            </MetaFilterSelectLabel>
            {metaFilterType === MetaFilterTypes.BY_NEW_ITEMS && (
              <MetaFilterDivider orientation="vertical" variant="middle" flexItem />
            )}
            <MetaFilterSelectIcon disableRipple>
              <MetaFilterSelectArrow />
            </MetaFilterSelectIcon>
          </MetaFilterSelectType>
          {metaFilterType !== MetaFilterTypes.DEFAULT && metaFilterType !== MetaFilterTypes.BY_NEW_ITEMS && (
            <MetaFilterDivider orientation="vertical" variant="middle" flexItem />
          )}
          {isSelectedTypeOneOf([MetaFilterTypes.BY_DATE_RANGE]) && eventsDatesRange && (
            <DateRangeSelect
              selectedDateFrom={dateRangeStart}
              selectedDateTo={dateRangeEnd}
              eventsDatesRange={eventsDatesRange}
              setDateRangeSelectEnd={setDateRangeEnd}
              setDateRangeSelectStart={setDateRangeStart}
            />
          )}
          {isSelectedTypeOneOf([MetaFilterTypes.BY_EVENT, MetaFilterTypes.BY_RECENT]) && (
            <MetaFilterSelectType>
              <MetaFilterSelectComponent
                value={selectedFilter?.value}
                onChange={handleSubmitFilterData}
                IconComponent={MetaFilterSelectArrow}
                onOpen={handleOpenSelect}
                onClose={handleCloseSelect}
                MenuProps={MenuProps}
              >
                {isSelectedTypeOneOf([MetaFilterTypes.BY_RECENT]) &&
                  recentItemOptions.map(item => (
                    <MetaFilterSelectMenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MetaFilterSelectMenuItem>
                  ))}
              </MetaFilterSelectComponent>
            </MetaFilterSelectType>
          )}
        </MetaFilterSelectContent>
        {isSelectedTypeOneOf([MetaFilterTypes.BY_DATE_RANGE]) && (
          <MetaFilterSelectSubmit variant="contained" onClick={handleSubmitDateRange}>
            {t(translationKeys.SEARCH_FILTER_SELECT_SUBMIT)}
          </MetaFilterSelectSubmit>
        )}
      </MetaFilterSelectWrapper>
      {!!metaFilterPopoverOpen && (
        <MetaFilterPopover
          id={id}
          isFilterByNewEnabled={isFilterByNewEnabled}
          open={metaFilterPopoverOpen}
          anchorEl={anchorEl}
          dateRangeStart={dateRangeStart}
          dateRangeEnd={dateRangeEnd}
          eventsDatesRange={eventsDatesRange}
          handleSelectMetaFilter={handleSelectMetaFilter}
          setDateRangeStart={setDateRangeStart}
          setDateRangeEnd={setDateRangeEnd}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          selectedFilter={selectedFilter}
          setMetaFilterType={setMetaFilterType}
          handleClose={handleCloseMetaFilter}
        />
      )}
    </MetaFilterWrapper>
  )
}

export default MetaFilterSelect
